import GroupIcon from '@mui/icons-material/Group';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    openFilterUserAction,
    selectFilteredUserData,
} from '../../../app/Slices/UserFilterSlice';
import {
    isDisabledCardUser,
    isEnabledCardUser,
    isPendingInviteForStats,
    type UsersStatsUserRow,
} from './usersStatsFilter';

export default function UsersStatsCards({
    users,
}: {
    users: UsersStatsUserRow[];
}) {
    const theme = useTheme();
    const dispatch = useDispatch();
    const filterResults = useSelector(selectFilteredUserData) as {
        role?: string;
        status?: string;
        team?: string;
        search?: string;
    } | null;

    const { total, active, pending, disabled } = useMemo(() => {
        const totalN = users.length;
        let activeN = 0;
        let pendingN = 0;
        let disabledN = 0;
        for (const u of users) {
            if (isPendingInviteForStats(u)) {
                pendingN += 1;
                continue;
            }
            if (u.isActive) activeN += 1;
            else disabledN += 1;
        }
        return {
            total: totalN,
            active: activeN,
            pending: pendingN,
            disabled: disabledN,
        };
    }, [users]);

    const applyStatusFilter = useCallback(
        (status: 'All' | 'enabled' | 'pendingInvite' | 'disabled') => {
            const prev = filterResults ?? {};
            dispatch(
                openFilterUserAction({
                    filteredData: {
                        role: prev.role ?? 'All',
                        team: prev.team ?? 'All',
                        search: prev.search ?? '',
                        status,
                    },
                }),
            );
        },
        [dispatch, filterResults],
    );

    const currentStatus = filterResults?.status ?? 'All';

    const activePct = total > 0 ? ((active / total) * 100).toFixed(1) : '0';
    const disabledPct = total > 0 ? ((disabled / total) * 100).toFixed(1) : '0';

    const card = (
        icon: React.ReactNode,
        iconBg: string,
        label: string,
        value: string | number,
        hint?: string,
        iconColor: string = 'primary.main',
        options?: {
            onClick?: () => void;
            selected?: boolean;
            ariaLabel?: string;
        },
    ) => (
        <Paper
            variant='outlined'
            onClick={options?.onClick}
            role={options?.onClick ? 'button' : undefined}
            tabIndex={options?.onClick ? 0 : undefined}
            onKeyDown={(e) => {
                if (!options?.onClick) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    options.onClick();
                }
            }}
            aria-label={options?.ariaLabel}
            aria-pressed={options?.selected}
            sx={{
                flex: { xs: 'none', sm: '1 1 160px' },
                minWidth: 0,
                width: '100%',
                p: 2,
                borderRadius: 2,
                borderColor: options?.selected ? 'primary.main' : 'divider',
                boxShadow: options?.selected
                    ? (t) => `0 0 0 1px ${t.palette.primary.main}`
                    : 'none',
                ...(options?.onClick
                    ? {
                          cursor: 'pointer',
                          transition: (t) =>
                              t.transitions.create(
                                  [
                                      'border-color',
                                      'box-shadow',
                                      'background-color',
                                  ],
                                  { duration: t.transitions.duration.short },
                              ),
                          '&:hover': {
                              borderColor: 'primary.light',
                              bgcolor: 'action.hover',
                          },
                      }
                    : {}),
            }}
        >
            <Stack direction='row' spacing={1.5} alignItems='flex-start'>
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: iconBg,
                        color: iconColor,
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant='body2' color='text.secondary'>
                        {label}
                    </Typography>
                    <Typography
                        variant='h5'
                        sx={{ fontWeight: 700, mt: 0.25, lineHeight: 1.2 }}
                    >
                        {value}
                    </Typography>
                    {hint ? (
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                mt: 0.5,
                                fontWeight: 400,
                            }}
                        >
                            {hint}
                        </Typography>
                    ) : null}
                </Box>
            </Stack>
        </Paper>
    );

    return (
        <Box
            sx={{
                width: '100%',
                display: { xs: 'grid', sm: 'flex' },
                gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'none',
                },
                flexDirection: { sm: 'row' },
                flexWrap: { sm: 'wrap' },
                gap: 2,
            }}
        >
            {card(
                <GroupIcon sx={{ fontSize: 22 }} />,
                alpha(theme.palette.primary.main, 0.12),
                'Total users',
                total,
                undefined,
                'primary.main',
                {
                    onClick: () => applyStatusFilter('All'),
                    selected: currentStatus === 'All',
                    ariaLabel: 'Show all users',
                },
            )}
            {card(
                <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />,
                alpha(theme.palette.success.main, 0.15),
                'Enabled',
                active,
                `${activePct}% of users`,
                'primary.main',
                {
                    onClick: () => applyStatusFilter('enabled'),
                    selected: currentStatus === 'enabled',
                    ariaLabel: 'Filter to enabled users',
                },
            )}
            {card(
                <SendOutlinedIcon
                    sx={{
                        fontSize: 22,
                        transform: 'rotate(-38deg)',
                        transformOrigin: 'center',
                    }}
                />,
                lighten(theme.palette.yellow.main, 0.82),
                'Pending invites',
                pending,
                'Invite not accepted',
                theme.palette.yellow.main,
                {
                    onClick: () => applyStatusFilter('pendingInvite'),
                    selected: currentStatus === 'pendingInvite',
                    ariaLabel: 'Filter to pending invites',
                },
            )}
            {card(
                <PersonOffIcon sx={{ fontSize: 22 }} />,
                alpha(theme.palette.error.main, 0.1),
                'Disabled',
                disabled,
                `${disabledPct}% of users`,
                'primary.main',
                {
                    onClick: () => applyStatusFilter('disabled'),
                    selected: currentStatus === 'disabled',
                    ariaLabel: 'Filter to disabled users',
                },
            )}
        </Box>
    );
}
